import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { MUSIC24_TIER_RANK } from 'fym-common/src/license/music24Plans';
import { useMyTeamRoleStatus } from '../../team/useMyTeamRole';
import { useLicenseInfo } from '../../../redux/slices/license/hooks/hooks';
import { toggleSMLPricingModal } from '../../../redux/slices/appInfo/slice';
import { MUSIC24_PLANS } from '../config/music24StripePlans';
import { clearCheckoutIntent, readCheckoutIntent } from '../config/checkoutIntent';
import { useMusic24Plan } from './useMusic24Plan';

export interface UseOpenCheckoutIntentPricingModalOptions {
  /** `false` on a public or pricing-hidden page (`SMLContainer`'s `publicMode`/`hidePricingModal`)
   * or before sign-in — nothing opens there, and the intent stays put for the next eligible page. */
  enabled: boolean;
  /** `true` while the welcome page is still due or the tour runs. The window opens after the
   * welcome page and the tour, never over them. It stays `true` until `SMLContainer` knows that
   * the welcome page is done and no tour runs. */
  blockedByOnboarding: boolean;
}

/**
 * Opens the plan window once for a visitor who arrived through a website "Choose <plan>" button
 * (ruling C2 — `readCheckoutIntent()` used to sit unread after sign-in, so a paid CTA landed the
 * visitor in the Trial with nothing offered). `SMLPricing` already reads the same intent for the
 * starting interval and the highlighted card, so this hook only has to decide whether, and when,
 * to raise the curtain.
 *
 * Runs its check at most once per mount, and only once nothing is still making it wait (the team
 * role, the welcome page and the tour): a team member is never offered a checkout (design o5's member
 * state already covers what a member sees, so nothing here needs to reopen it for them), an
 * unrecognised plan id or a member's intent clears the intent silently — a member cannot buy, and
 * a kept intent could otherwise fire much later after an unrelated role change. A trial is not a
 * paid plan (every new sign-up starts on it), so only an *active paid* subscription at or above
 * the intent's plan clears the intent without opening the window; a Free or trialing visitor with
 * any paid intent gets the window, with that plan and interval already selected. Every other case
 * opens the window and leaves the intent for `SMLPricing`/the checkout flow to consume and clear.
 */
export function useOpenCheckoutIntentPricingModal({
  enabled,
  blockedByOnboarding,
}: UseOpenCheckoutIntentPricingModalOptions): void {
  const dispatch = useDispatch();
  // Off on a public or pricing-hidden shell: nothing opens there, so there is no role to ask for.
  const { role, isResolved } = useMyTeamRoleStatus({ enabled });
  const currentPlan = useMusic24Plan();
  const licenseInfo = useLicenseInfo();
  const hasChecked = useRef(false);

  useEffect(() => {
    if (hasChecked.current || !enabled || !isResolved || blockedByOnboarding) {
      // The role can still be loading, or the welcome page can still be due or the tour can still run — wait for the real
      // answer instead of guessing, so an owner never loses the window just because `team.list`
      // was slow, a member never sees one flash open before the answer arrives, and the window
      // never opens over the welcome page or the tour.
      return;
    }
    const intent = readCheckoutIntent();
    if (intent === null) {
      return;
    }
    hasChecked.current = true;

    const plan = MUSIC24_PLANS.find((candidate) => candidate.id === intent.plan);
    if (plan === undefined) {
      // A stale bookmark or an old email link naming a retired plan id. Nothing to open. In
      // practice `readCheckoutIntent()` already filters this through `parseCheckoutIntent`, so a
      // real `sessionStorage` entry never reaches this branch today — kept as this hook's own
      // contract in case a future caller ever hands it an unvalidated intent.
      clearCheckoutIntent();
      return;
    }
    if (role === 'MEMBER') {
      // A member cannot check out at all (ruling 8) — the plan window offers them nothing new.
      // Clear the intent rather than keep it: a member cannot buy, and a kept intent could
      // otherwise fire much later, after an unrelated role change turned them into the owner.
      clearCheckoutIntent();
      return;
    }
    // A trial is not a paid plan — every new sign-up starts on it, so treating it as "already
    // holds Manager" would drop every Artist/Manager intent from the website. Only an *active
    // paid* subscription (never free, never trial, and not lapsed) at or above the intent's plan
    // counts as already holding it.
    const hasActivePaidPlanAtOrAbove =
      currentPlan !== 'free' &&
      currentPlan !== 'trial' &&
      licenseInfo.isSubscriptionValid() &&
      MUSIC24_TIER_RANK[currentPlan] >= MUSIC24_TIER_RANK[plan.tier];
    if (hasActivePaidPlanAtOrAbove) {
      // Already on that plan, or on a higher one — nothing left to sell.
      clearCheckoutIntent();
      return;
    }
    dispatch(toggleSMLPricingModal({ shouldShow: true }));
  }, [blockedByOnboarding, currentPlan, dispatch, enabled, isResolved, licenseInfo, role]);
}
